import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { API_ENDPOINT, InvoiceStatus } from '../constants';
import { fetchWrapper } from '../helpers';
import { getInvoiceSearchStr } from '../utils/common';

import type { IClaimInvoice, IClaimInvoiceList, IInvoiceDetails, IOption } from '../utils/types';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';

const name = 'invoices';
const initialState = createInitialState();
const reducers = createReducers();
// SAFETY: Legacy thunk action creators use the store dispatch result contract.
const extraActions = createExtraActions() as any;
const slice = createSlice({
  name,
  initialState,
  reducers,
  extraReducers: (builder: ActionReducerMapBuilder<InvoiceState>) => createExtraReducers(builder),
});

export interface InvoiceState {
  list: IClaimInvoiceList;
  clientNotPaidInvoiceCount: { [clientId: number]: number };
  current: IInvoiceDetails | null;
  invoicePreview: IInvoiceDetails | null;
  selectedCleintToBuildInvoice: IOption | null;
  error: any;
  loading: boolean;
  pdfDownloading: boolean;
}

function createInitialState(): InvoiceState {
  return {
    list: {
      total_amount_due: 0,
      invoices: [],
    },
    clientNotPaidInvoiceCount: {},
    selectedCleintToBuildInvoice: null,
    invoicePreview: null,
    current: null,
    error: null,
    loading: false,
    pdfDownloading: false,
  };
}

function createReducers() {
  return { clearState, updateState };

  function clearState(state: InvoiceState) {
    state.current = null;
    state.invoicePreview = null;
    state.loading = false;
    state.error = null;
  }

  function updateState(state: InvoiceState, action: any) {
    const { key, data } = action.payload;
    Object.assign(state, { [key]: data });
  }
}

function createExtraActions() {
  const baseUrl = `${API_ENDPOINT}/claim_invoices`;

  return {
    createInvoice: createInvoice(),
    createInvoicePreview: createInvoicePreview(),
    createInvoicePayment: createInvoicePayment(),
    deleteInvoicePayment: deleteInvoicePayment(),
    getList: getList(),
    getInvoiceById: getInvoiceById(),
    sendInvoice: sendInvoice(),
    updateInvoicePayment: updateInvoicePayment(),
    resetInvoiceStatus: resetInvoiceStatus(),
    downloadInvoicePDF: downloadInvoicePDF(),
    makeInvoicePayment: makeInvoicePayment(),
  };

  function getList() {
    return createAsyncThunk(
      `${name}/getList`,
      async ({ params, getAccessTokenSilently, signal }: any) => {
        let baseSearch = `${baseUrl}/?`;

        if (params) {
          Object.keys(params).forEach((key, index) => {
            const paramStr = getInvoiceSearchStr(key, params[key]);
            if (paramStr) {
              baseSearch += (index === 0 ? '' : '&') + paramStr;
            }
          });
        }

        const response = await fetchWrapper.get({
          url: baseSearch,
          getAccessTokenSilently,
          signal,
        });
        return response;
      },
    );
  }

  function createInvoice() {
    return createAsyncThunk(
      `${name}/createInvoice`,
      async ({ data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.post({ url: `${baseUrl}/`, getAccessTokenSilently }, data);
      },
    );
  }

  function createInvoicePreview() {
    return createAsyncThunk(
      `${name}/createInvoicePreview`,
      async ({ data, getAccessTokenSilently }: any) => {
        return await fetchWrapper.post(
          { url: `${baseUrl}/preview/`, getAccessTokenSilently },
          data,
        );
      },
    );
  }

  function getInvoiceById() {
    return createAsyncThunk(
      `${name}/getInvoiceById`,
      async ({ getAccessTokenSilently, invoiceId, expand = [] }: any) => {
        const expandQueryString = expand.map((item: any) => `expand=${item}`).join('&');
        return await fetchWrapper.get({
          url: `${baseUrl}/${invoiceId}/?${expandQueryString}`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function createInvoicePayment() {
    return createAsyncThunk(
      `${name}/createInvoicePayment`,
      async ({ getAccessTokenSilently, invoiceId, data }: any) => {
        return await fetchWrapper.post(
          {
            url: `${baseUrl}/${invoiceId}/payments/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function sendInvoice() {
    return createAsyncThunk(
      `${name}/sendInvoice`,
      async ({ getAccessTokenSilently, invoiceId }: any) => {
        return await fetchWrapper.put({
          url: `${baseUrl}/${invoiceId}/send/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function updateInvoicePayment() {
    return createAsyncThunk(
      `${name}/updateInvoicePayment`,
      async ({ getAccessTokenSilently, invoiceId, paymentId, data }: any) => {
        return await fetchWrapper.patch(
          {
            url: `${baseUrl}/${invoiceId}/payments/${paymentId}/`,
            getAccessTokenSilently,
          },
          data,
        );
      },
    );
  }

  function deleteInvoicePayment() {
    return createAsyncThunk(
      `${name}/deleteInvoicePayment`,
      async ({ getAccessTokenSilently, invoiceId, paymentId }: any) => {
        return await fetchWrapper.delete({
          url: `${baseUrl}/${invoiceId}/payments/${paymentId}/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function resetInvoiceStatus() {
    return createAsyncThunk(
      `${name}/resetInvoiceStatus`,
      async ({ getAccessTokenSilently, invoiceId }: any) => {
        return await fetchWrapper.put({
          url: `${baseUrl}/${invoiceId}/reset_sent_status/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function downloadInvoicePDF() {
    return createAsyncThunk(
      `${name}/downloadInvoicePDF`,
      async ({ getAccessTokenSilently, invoiceId }: any) => {
        return await fetchWrapper.get({
          url: `${baseUrl}/${invoiceId}/download/`,
          getAccessTokenSilently,
        });
      },
    );
  }

  function makeInvoicePayment() {
    return createAsyncThunk(
      `${name}/makeInvoicePayment`,
      async ({ getAccessTokenSilently, invoiceId }: any) => {
        return await fetchWrapper.post({
          url: `${baseUrl}/${invoiceId}/pay/`,
          getAccessTokenSilently,
        });
      },
    );
  }
}

function createExtraReducers(builder: ActionReducerMapBuilder<InvoiceState>) {
  const {
    getList,
    getInvoiceById,
    createInvoice,
    sendInvoice,
    createInvoicePreview,
    createInvoicePayment,
    updateInvoicePayment,
    deleteInvoicePayment,
    resetInvoiceStatus,
    downloadInvoicePDF,
    makeInvoicePayment,
  } = extraActions;

  builder
    // getList()
    .addCase(getList.pending, (state: InvoiceState) => {
      state.loading = true;
      state.error = null;
    })

    .addCase(getList.fulfilled, (state: InvoiceState, action: any) => {
      if (action.meta.arg.params && action.meta.arg.params.not_paid_invoices) {
        const unpaidInvoices = action.payload.invoices.filter(
          (invoice: IClaimInvoice) =>
            invoice.status === InvoiceStatus.NOT_PAID ||
            invoice.status === InvoiceStatus.PARTIALLY_PAID,
        );

        // Group unpaid invoices by client_id and get count
        const countByClientId: { [clientId: number]: number } = {};
        unpaidInvoices.forEach((invoice: IClaimInvoice) => {
          const clientId = invoice.client.id;
          if (clientId !== undefined) {
            // Ensure clientId is defined
            if (!countByClientId[clientId]) {
              countByClientId[clientId] = 0;
            }
            countByClientId[clientId]++;
          }
        });
        state.clientNotPaidInvoiceCount = countByClientId;
      } else {
        state.list = action.payload;
        state.loading = false;
      }
    })

    .addCase(getList.rejected, (state: InvoiceState, action: any) => {
      state.list = {
        total_amount_due: 0,
        invoices: [],
      };
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // getInvoiceById()
    .addCase(getInvoiceById.pending, (state: InvoiceState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      getInvoiceById.fulfilled,
      (state: InvoiceState, action: PayloadAction<IInvoiceDetails>) => {
        state.current = action.payload;
        state.loading = false;
      },
    )
    .addCase(getInvoiceById.rejected, (state: InvoiceState, action: any) => {
      state.current = null;
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // sendInvoice
    .addCase(sendInvoice.pending, (state: InvoiceState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(sendInvoice.fulfilled, (state: InvoiceState, action: any) => {
      state.loading = false;
      state.current = action.payload;
      toast.success('Invoice sent successfully');
    })
    .addCase(sendInvoice.rejected, (state: InvoiceState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // createInvoice()
    .addCase(createInvoice.pending, (state: InvoiceState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createInvoice.fulfilled,
      (state: InvoiceState, action: PayloadAction<IInvoiceDetails>) => {
        state.current = action.payload;
        state.loading = false;
        toast.success('Invoice created');
      },
    )
    .addCase(createInvoice.rejected, (state: InvoiceState, action: any) => {
      state.current = null;
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message, { toastId: 'create-invoice-error' });
    })
    // createInvoicePreview()
    .addCase(createInvoicePreview.pending, (state: InvoiceState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(
      createInvoicePreview.fulfilled,
      (state: InvoiceState, action: PayloadAction<IInvoiceDetails>) => {
        state.invoicePreview = action.payload;
        state.loading = false;
      },
    )
    .addCase(createInvoicePreview.rejected, (state: InvoiceState, action: any) => {
      state.invoicePreview = null;
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // createInvoicePayment()
    .addCase(createInvoicePayment.pending, (state: InvoiceState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(createInvoicePayment.fulfilled, (state: InvoiceState, action: any) => {
      if (state.current) {
        const { invoice_amount_due, invoice_id: _invoice_id, ...paymentDetails } = action.payload;
        state.current = {
          ...state.current,
          amount_due: invoice_amount_due,
          payments: [...state.current.payments, paymentDetails],
        };
      }
      state.loading = false;
    })
    .addCase(createInvoicePayment.rejected, (state: InvoiceState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // updateInvoicePayment()
    .addCase(updateInvoicePayment.pending, (state: InvoiceState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(updateInvoicePayment.fulfilled, (state: InvoiceState, action: any) => {
      const { paymentId } = action.meta.arg;
      if (state.current) {
        const { invoice_amount_due, invoice_id: _invoice_id, ...paymentDetails } = action.payload;
        state.current = {
          ...state.current,
          payments: state.current.payments.map((payment) => {
            if (payment.id === paymentId) {
              return paymentDetails;
            }
            return payment;
          }),
          amount_due: invoice_amount_due,
        };
      }
      state.loading = false;
    })
    .addCase(updateInvoicePayment.rejected, (state: InvoiceState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })
    // deleteInvoicePayment()
    .addCase(deleteInvoicePayment.pending, (state: InvoiceState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(deleteInvoicePayment.fulfilled, (state: InvoiceState, action: any) => {
      const { invoice_amount_due, invoice_id } = action.payload;

      if (state.current && state.current.id === invoice_id) {
        const { paymentId } = action.meta.arg;
        state.current = {
          ...state.current,
          payments: state.current.payments.filter((payment) => payment.id !== paymentId),
          amount_due: invoice_amount_due,
        };
      }
      state.loading = false;
    })
    .addCase(deleteInvoicePayment.rejected, (state: InvoiceState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // resetInvoiceStatus()
    .addCase(resetInvoiceStatus.pending, (state: InvoiceState) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(resetInvoiceStatus.fulfilled, (state: InvoiceState, action: any) => {
      state.current = action.payload;
      state.loading = false;
      toast.success('Invoice status reset successfully');
    })
    .addCase(resetInvoiceStatus.rejected, (state: InvoiceState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    })

    // downloadInvoicePDF()
    .addCase(downloadInvoicePDF.pending, (state: InvoiceState) => {
      state.pdfDownloading = true;
      state.error = null;
    })
    .addCase(downloadInvoicePDF.fulfilled, (state: InvoiceState) => {
      state.pdfDownloading = false;
      toast.success('Invoice PDF downloaded successfully');
    })
    .addCase(downloadInvoicePDF.rejected, (state: InvoiceState, action: any) => {
      state.error = action.error;
      state.pdfDownloading = false;
      toast.error(action.error.message);
    })
    // makeInvoicePayment()
    .addCase(makeInvoicePayment.pending, (state: InvoiceState) => {
      state.loading = true;
    })
    .addCase(makeInvoicePayment.fulfilled, (state: InvoiceState) => {
      state.loading = false;
    })
    .addCase(makeInvoicePayment.rejected, (state: InvoiceState, action: any) => {
      state.error = action.error;
      state.loading = false;
      toast.error(action.error.message);
    });
}

export const claimsInvoiceActions = { ...slice.actions, ...extraActions };
export const claimInvoiceReducer = slice.reducer;
